import React, { ReactNode, useEffect, useReducer } from 'react';
import { ViewerItem } from 'presentations/api/schema/user';
import { authorize } from 'presentations/views/helpers/auth/session';
import { useRouter } from 'next/router';
import { isSkipAuthUrl } from 'presentations/routes/helper';

declare global {
  interface Window {
    webkit: any;
  }
}

export interface Props {
  children: ReactNode;
}

export interface State {
  viewer: ViewerItem | null;
  isInitialized: boolean;
}

const initialState: State = {
  viewer: null,
  isInitialized: false,
};

export const setViewer = (viewer: ViewerItem | null) =>
  ({
    type: 'SET_VIEWER',
    viewer,
  } as const);

export type Action = ReturnType<typeof setViewer>;

const reducer = (state: State = initialState, action: Action) => {
  switch (action.type) {
    case 'SET_VIEWER':
      if (
        action.viewer &&
        typeof window.webkit?.messageHandlers?.setViewer?.postMessage ===
          'function'
      ) {
        window.webkit.messageHandlers.setViewer.postMessage(action.viewer);
      }
      return { ...state, viewer: action.viewer, isInitialized: true };
    default:
      return state;
  }
};

const ServicePortalContext = React.createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: initialState,
  dispatch: () => {},
});

const ServicePortalProvider = (props: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchViewer = async () => {
    try {
      const user = await authorize();
      dispatch(setViewer(user));
    } catch (error) {
      console.debug(error);
    }
  };

  const router = useRouter();

  useEffect(() => {
    if (isSkipAuthUrl(router.asPath)) {
      dispatch(setViewer(null));
      return;
    }
    fetchViewer();
  }, []);

  return (
    <ServicePortalContext.Provider value={{ state, dispatch }}>
      {props.children}
    </ServicePortalContext.Provider>
  );
};

export { ServicePortalContext, ServicePortalProvider };
